/**
 * Aircraft size tiers — the shared taxonomy that maps `aircraft_types.category`
 * to a coarse size band. Used by the synthetic fleet generator (mix weights),
 * the pricing model (rates scale by tier), and the stats engine (fleet mix).
 *
 * Kept in its own module so the generator and the stats/pricing layer agree on
 * one source of truth (no duplicated category→tier maps drifting apart).
 */

export type Tier = 'heavy' | 'super_mid' | 'mid' | 'light' | 'turboprop' | 'piston'

/** Which `aircraft_types.category` values fall in each tier. */
export const TIER_CATEGORIES: Record<Tier, string[]> = {
  heavy: ['large_jet', 'ultra_long_range'],
  super_mid: ['super_midsize_jet'],
  mid: ['midsize_jet'],
  light: ['light_jet', 'very_light_jet'],
  turboprop: ['turboprop'],
  piston: ['single_engine_piston', 'multi_engine_piston'],
}

/** Display order (largest → smallest), used for axes, legends, and rate grids. */
export const TIER_ORDER: Tier[] = ['heavy', 'super_mid', 'mid', 'light', 'turboprop', 'piston']

export const TIER_LABELS: Record<Tier, string> = {
  heavy: 'Heavy / ULR',
  super_mid: 'Super-mid',
  mid: 'Midsize',
  light: 'Light',
  turboprop: 'Turboprop',
  piston: 'Piston',
}

/** Reverse lookup category → tier. Defaults to `mid` for unknown/null categories. */
export function tierForCategory(category: string | null | undefined): Tier {
  if (category) {
    for (const tier of TIER_ORDER) {
      if (TIER_CATEGORIES[tier].includes(category)) return tier
    }
  }
  return 'mid'
}
